import React, { useEffect, useState } from 'react';
import { Participant } from '../../../core/models/Participant';
import ItemParticipant from '../items/Participan';
import IndicatorDots from '../utils/IndicatorDots';
import { useDataContext } from '../../../core/context/DataContext';
import CardAnimated from '../cards/CardAnimated';
interface Props {
  boxId: number;
  date: string; // 'YYYY-MM-DD'
  time: string; // 'HH:mm:ss'
  schedule: number[] | undefined;
  onEmpty?: () => void; // <-- callback opcional
}

const ParticipantList: React.FC<Props> = ({ boxId, date, time, schedule, onEmpty }) => {
  const { fetchParticipantsForSchedules, participantsVersion } = useDataContext();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [participantsBySchedule, setParticipantsBySchedule] = useState<
    Record<number, Participant[]>
  >({});
  const [combined, setCombined] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function load() {
      // Solo mostrar loading en la primera carga
      if (isFirstLoad) {
        setLoading(true);
      }

      setError(null);

      // No limpiar arrays en refrescos para evitar flicker
      if (isFirstLoad) {
        setParticipantsBySchedule({});
        setCombined([]);
      }

      if (!schedule || schedule.length === 0) {
        setLoading(false);
        if (onEmpty) onEmpty();
        return;
      }

      try {
        // Llamamos al provider para traer los participantes (usa cache interno)
        // time = '09:30:00'; // moc para testear
        const map = await fetchParticipantsForSchedules(schedule, date, time);

        if (!mounted) {
          return;
        }

        // combinar y eliminar duplicados por id
        const byId = new Map<number, Participant>();
        Object.values(map)
          .flat()
          .forEach((p) => {
            if (p && typeof p.id === 'number' && !byId.has(p.id)) {
              byId.set(p.id, p);
            }
          });
        const combinedList = Array.from(byId.values());

        setParticipantsBySchedule(map);
        setCombined(combinedList);

        if (combinedList.length === 0 && onEmpty) {
          onEmpty();
        }
      } catch (err: any) {
        console.error('Error loading participants via provider:', err);
        setError(err?.message ?? 'Error inesperado');
        if (onEmpty) onEmpty();
      } finally {
        if (mounted) {
          setLoading(false);
          // Marcar que ya no es la primera carga
          if (isFirstLoad) {
            setIsFirstLoad(false);
          }
        }
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [
    schedule,
    date,
    time,
    fetchParticipantsForSchedules,
    onEmpty,
    participantsVersion,
    isFirstLoad,
  ]);

  if (loading) return <></>;
  if (error) return <></>;
  if (!schedule || schedule.length === 0) return null;

  if (!combined || combined.length === 0) return null;

  return (
    <CardAnimated>
      <>
        {combined.length > 0 ? (
          <>
            <div className="flex items-center justify-between w-full mb-6 pr-2">
              <h2 className="flex-col flex tv-text-2xl font-semibold mb-0">
                {combined.length} {combined.length === 1 ? 'Participante' : 'Participantes'}
                {/* Mostrar rango de tiempo si existe */}
                {combined.length > 0 && combined[0].start_time && combined[0].end_time && (
                  <span className="tv-text-base font-normal text-gray-300/50 ml-0">
                    {combined[0].start_time.slice(0, 5)} a {combined[0].end_time.slice(0, 5)}
                  </span>
                )}
              </h2>
              <IndicatorDots />
            </div>

            <ul className="grid grid-cols-4 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {combined.map((p, index) => {
                const firstName = `${p.user?.profile?.name ?? ''}`.trim();
                /* const lastName =
                  `${p.user?.profile?.first_surname ?? ''} ${p.user?.profile?.second_surname ?? ''}`.trim(); */
                const displayName = [firstName].filter(Boolean).join('\n');

                return (
                  <li key={p.id} className="flex flex-col items-center gap-3">
                    <ItemParticipant
                      img={
                        p.user?.profile?.pictureUrl ??
                        `https://randomuser.me/api/portraits/men/${Number(p.id) % 10}.jpg`
                      }
                      name={displayName}
                    />
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <></>
        )}
      </>
    </CardAnimated>
  );
};
export default ParticipantList;
